import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Pill } from "@/components/podkit/status/Pill";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Text } from "@/components/podkit/typography/Text";
import { CloudFormationStackCopyDetails } from "@/components/runners/details/CloudFormationStackCopyDetails";
import { CloudFormationStackCreateButton } from "@/components/runners/details/CloudFormationStackCreateButton";
import { CloudFormationStackDetails } from "@/components/runners/details/CloudFormationStackDetails";
import type { PlainMessage } from "@bufbuild/protobuf";
import IconLoader from "central-icons/IconLoader";
import { type Runner } from "gitpod-next-api/gitpod/v1/runner_pb";
import { parseAsBoolean, useQueryState } from "nuqs";
import { type FC } from "react";

export const CloudFormationStack: FC<{ runner: PlainMessage<Runner>; hasStack: boolean }> = ({ runner, hasStack }) => {
    return (
        <div className="flex flex-col">
            {!hasStack && (
                <div className="flex flex-col gap-1">
                    <Text className="text-base">
                        We&#x2019;ll help you set up a new runner on your AWS EC2 infrastructure. The link below will
                        take you to AWS CloudFormation with a set of pre-populated values to quickly get everything
                        working.{" "}
                        <ExternalLink
                            className="font-bold"
                            href="https://ona.com/docs/ona/runners/aws/setup#cloudformation-template-deployment"
                        >
                            Read the docs.
                        </ExternalLink>
                    </Text>
                </div>
            )}
            <CloudFormationStackContent runner={runner} hasStack={hasStack} />
        </div>
    );
};

const CloudFormationStackContent: FC<
    { runner: PlainMessage<Runner> | undefined; hasStack: boolean } & PropsWithClassName
> = ({ runner, hasStack, className }) => {
    return (
        <SkeletonBlock ready={Boolean(runner)} className="h-[150px]">
            {runner && (
                <div className="flex flex-col">
                    <div className={cn("rounded-lg", className)}>
                        {hasStack ? (
                            <CloudFormationStackDetails runner={runner} />
                        ) : (
                            <CloudFormationStackCreate runner={runner} />
                        )}
                    </div>
                </div>
            )}
        </SkeletonBlock>
    );
};

const CloudFormationStackCreate: FC<{ runner: PlainMessage<Runner> }> = ({ runner }) => {
    const [showOnboarding, setShowOnboarding] = useQueryState("showOnboarding", parseAsBoolean.withDefault(false));

    return (
        <div className="flex flex-col text-left">
            <div className="flex flex-col">
                <ul className="my-6 ml-8 list-decimal space-y-6">
                    <li className="space-y-1">
                        <span>Deploy your runner</span>
                        <Text className="text-md" muted>
                            This step opens a pre-configured CloudFormation template in your AWS account. The template
                            defines all the resources needed for Ona to connect securely to your infrastructure.
                        </Text>
                        <Text className="text-md" muted>
                            Once you deploy it, AWS will provision the runner and link it back to your Ona organization.
                        </Text>
                        <div>
                            <CloudFormationStackCreateButton
                                runner={runner}
                                text="AWS CloudFormation Template"
                                className="my-2"
                                onClicked={() => setShowOnboarding(false)}
                            />
                            <Text className="text-md" muted>
                                Pre-configured for your organization. Review and deploy it in your AWS Console.
                            </Text>
                            <div className="mt-4 flex flex-col gap-1">
                                <Text className="text-md">Need someone else to deploy it?</Text>
                                <CloudFormationStackCopyDetails
                                    runner={runner}
                                    onClicked={() => setShowOnboarding(false)}
                                />
                            </div>
                        </div>
                    </li>
                    {showOnboarding ? (
                        <li className="space-y-1">
                            <span>
                                Once you&apos;ve executed the CloudFormation template in AWS, please return to this
                                page.
                            </span>
                        </li>
                    ) : (
                        <li className="space-y-1">
                            <span>Wait for the connection</span>

                            <Text className="text-md" muted>
                                After the CloudFormation stack finishes, Ona will automatically detect and register your
                                runner.
                            </Text>
                            <Text className="text-md" muted>
                                This usually takes about 2 to 3 minutes.
                            </Text>
                            <div className="pt-2" />
                            <Pill
                                variant="neutral"
                                className="mt-4"
                                LeadingIcon={() => (
                                    <IconLoader className="animate-[spin_3s_linear_infinite]" size={16} />
                                )}
                            >
                                Waiting for connection
                            </Pill>
                        </li>
                    )}
                </ul>
            </div>
        </div>
    );
};
